import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import classnames from 'classnames';
import { head } from 'lodash';

import { Container, ServicingSection } from 'web-ui-components/lib/global/layout';
import { ValidationMessage } from 'web-ui-components/lib/molecules/forms';

import withContent from '../../../containers/withContent';
import { withScrollTop } from '../../../containers/withScrollTop';
import withAccountSelection from '../../../containers/withAccountSelection';

import { contentWrapper as CreditTable } from '../../../bacs/components/BacsTable/CreditTable/CreditTable';

import { hasApiCallInProgress } from '../../../states/api/selectors';
import { showAllErrors, updateValidation, hideErrorMsg } from '../../../states/validation/actions';
import { submitImportedPayeesForBacsPaymentRequest } from '../../../managePayee/states/action';
import { formValid, getValidationRowState } from '../../../states/validation/selectors';
import { fromAccountPropType } from '../../../states/accounts/propTypes';
import {
	getAccountsForBacsOnType,
	getBacsIdsByAccountIdAndBacsType,
	getAccountsForAddPayee,
} from '../../../states/accounts/selectors';
import {
	updateFromAccount,
	updateBacsId,
	updatePaymentName,
	updateSubmissionDate,
	addMultiplePayments,
	initialiseBacsCredit,
} from '../../../states/bacsCredit/actions';
import {
	getFromAccount,
	getBacsPaymentName,
	getSubmissionDate,
	getSettlementDate,
	getPayments,
	getBacsId,
	getTotalLimitExceeded,
	getBacsLimit,
} from '../../../states/bacsCredit/selectors';
import pageContent, { contentPropTypes } from './importedPayments.content';
import styles from '../../../bacs/components/BacsPaymentsPage/BacsPaymentStyles';
import { BACS_CREDIT_CODE, TYPE_BACS_CREDIT } from '../../../config';
import { CONST_FILE_UPLOAD } from '../../../config/constants';
import UnSavedWarningModal from '../../../sharedComponents/UnSavedWarningModal/UnSavedWarningModal';
import { accountsForAddPayeePropTypes } from '../../../payees/states/addPayee/propTypes';
import { BacsUploadHeader, BacsUploadFooter, BacsUploadBody, getPageTotal } from '../common';
import { cleanIllegalBACSCharacters } from '../../../helpers/fieldHelpers';

const mapState = (state) => ({
	fromAccounts: getAccountsForBacsOnType(state, BACS_CREDIT_CODE),
	bacsIds: getBacsIdsByAccountIdAndBacsType(state, getFromAccount(state), BACS_CREDIT_CODE),
	bacsLimit: getBacsLimit(state),
	submissionDate: getSubmissionDate(state),
	settlementDate: getSettlementDate(state),
	hasApiCallInProgress: hasApiCallInProgress(state),
	isFormValid: formValid(state),
	paymentName: getBacsPaymentName(state),
	validations: getValidationRowState(state, 'from'),
	payments: getPayments(state),
	fromAccount: getFromAccount(state),
	bacsId: getBacsId(state),
	totalLimitExceeded: getTotalLimitExceeded(state),
	accountsForAddPayee: getAccountsForAddPayee(state),
});

const mapDispatch = (dispatch) => ({
	updateFromAccount: (accountId) => dispatch(updateFromAccount({ accountId })),
	handleBacsIdChange: (bacsId) => dispatch(updateBacsId({ bacsId })),
	handlePaymentNameChange: (paymentName) =>
		dispatch(updatePaymentName({ paymentName: cleanIllegalBACSCharacters(paymentName) })),
	addMultiplePayments: (payments) => dispatch(addMultiplePayments(payments)),
	showAllErrors: () => dispatch(showAllErrors()),
	updateValidation: (key, isValid) =>
		dispatch(
			updateValidation({
				id: 'from',
				key,
				isValid,
			})
		),
	hideErrorMsg: (key) =>
		dispatch(
			hideErrorMsg({
				id: 'from',
				key,
			})
		),
	handleDateChange: (submissionDate) => {
		dispatch(updateSubmissionDate({ submissionDate }));
	},
	initialiseBacsCredit: (bacsDetails, payments) => dispatch(initialiseBacsCredit(bacsDetails, payments)),
	submitImportedPayeesForBacsPaymentRequest: (details) => dispatch(submitImportedPayeesForBacsPaymentRequest(details)),
});

class CreditImportedPaymentsPage extends Component {
	constructor(props) {
		super(props);

		this.state = {
			showUnsavedWarningModal: false,
		};
		this.handleContinue = this.handleContinue.bind(this);
		this.handleSelectFromAccount = this.handleSelectFromAccount.bind(this);
		this.handleCancel = this.handleCancel.bind(this);
	}

	componentWillMount() {
		const { initialiseBacsCredit } = this.props;
		this.props.handlePaymentInReview(true);
		initialiseBacsCredit({ batch_type: CONST_FILE_UPLOAD.BATCH_TYPE }, []);
	}
	componentDidMount() {
		const { bacsIds, handleBacsIdChange, handleDisplayFullwidth, handlePaymentNameChange, importedPaymentFileName } =
			this.props;
		handleDisplayFullwidth();
		handlePaymentNameChange(importedPaymentFileName);
		if (bacsIds.length === 1) {
			handleBacsIdChange(head(bacsIds));
		}
	}
	componentDidUpdate(prevProps) {
		const { fromAccount, bacsIds, handleBacsIdChange } = this.props;
		const fromAccountChanged = fromAccount !== prevProps.fromAccount;
		if (fromAccountChanged && bacsIds.length === 1) {
			handleBacsIdChange(head(bacsIds));
		}
	}
	componentWillUnmount() {
		this.props.handlePaymentInReview(false);
	}

	handleContinue() {
		const {
			isFormValid,
			showAllErrors,
			handleContinue,
			handleBack,
			addMultiplePayments,
			importedPaymentsData,
			submitImportedPayeesForBacsPaymentRequest,
			accountsForAddPayee,
		} = this.props;
		showAllErrors();

		if (isFormValid) {
			const details = {
				importedPaymentsData,
				accountsForAddPayee,
				addMultiplePayments,
				handleContinue,
				handleBack,
				type: TYPE_BACS_CREDIT,
			};
			submitImportedPayeesForBacsPaymentRequest(details);
		}
	}

	handleSelectFromAccount(accountId) {
		if (accountId !== this.props.fromAccount) {
			this.props.updateFromAccount(accountId);
		}
	}

	handleCancel() {
		this.setState({
			showUnsavedWarningModal: true,
		});
	}

	render() {
		const {
			content,
			importedPaymentsData,
			hasApiCallInProgress,
			totalLimitExceeded,
			handleBack,
			showFromAccountSelector,
			handleFromCardClick,
		} = this.props;

		const { title, description } = content;

		const { showUnsavedWarningModal } = this.state;

		const wrapperClass = classnames({
			'c-o-payee-list--from-state': showFromAccountSelector,
		});

		return (
			<div className={wrapperClass}>
				{showUnsavedWarningModal && (
					<UnSavedWarningModal
						handleClose={() => {
							this.setState({ showUnsavedWarningModal: false });
						}}
						isModalOpen
						handleBack={handleBack}
						contentKey="importPaymentsContent"
					/>
				)}
				<BacsUploadHeader title={title} description={description} />
				<div className="u-background-color-grey-tint-95 u-padding-3">
					<BacsUploadBody
						{...this.props}
						showFromAccountSelector={showFromAccountSelector}
						handleSelectFromAccount={this.handleSelectFromAccount}
						handleFromCardClick={handleFromCardClick}
						isCredit
					/>
					<ServicingSection padded={false}>
						<Container className={styles.payments__container}>
							<CreditTable
								bacsDetails={{ payments: importedPaymentsData, total: getPageTotal(importedPaymentsData) }}
								showTotal
							/>
							{totalLimitExceeded && <ValidationMessage hasIcon={false} value={content.maxLimitExceededMessage} />}
							<BacsUploadFooter
								isBatchLimitExceeded={totalLimitExceeded}
								hasApiCallInProgress={hasApiCallInProgress}
								handleContinue={this.handleContinue}
								handleCancel={this.handleCancel}
							/>
						</Container>
					</ServicingSection>
				</div>
			</div>
		);
	}
}

CreditImportedPaymentsPage.propTypes = {
	accountsForAddPayee: PropTypes.arrayOf(PropTypes.shape(accountsForAddPayeePropTypes)).isRequired,
	addMultiplePayments: PropTypes.func.isRequired,
	bacsIds: PropTypes.arrayOf(PropTypes.string).isRequired,
	content: PropTypes.shape(contentPropTypes).isRequired,
	fromAccount: PropTypes.string,
	fromAccounts: PropTypes.arrayOf(PropTypes.shape(fromAccountPropType)).isRequired,
	handleBack: PropTypes.func.isRequired,
	handleBacsIdChange: PropTypes.func.isRequired,
	handleContinue: PropTypes.func.isRequired,
	handleDisplayFullwidth: PropTypes.func.isRequired,
	handlePaymentInReview: PropTypes.func.isRequired,
	handlePaymentNameChange: PropTypes.func.isRequired,
	updateFromAccount: PropTypes.func.isRequired,
	hasApiCallInProgress: PropTypes.bool.isRequired,
	importedPaymentFileName: PropTypes.string,
	importedPaymentsData: PropTypes.arrayOf(PropTypes.string.isRequired),
	initialiseBacsCredit: PropTypes.func,
	isFormValid: PropTypes.bool.isRequired,
	showAllErrors: PropTypes.func.isRequired,
	submitImportedPayeesForBacsPaymentRequest: PropTypes.func.isRequired,
	totalLimitExceeded: PropTypes.bool,
	isLargeCustomerView: PropTypes.bool.isRequired,
	handleFromCardClick: PropTypes.func.isRequired,
	showFromAccountSelector: PropTypes.bool.isRequired,
};

export const connectWrapper = compose(
	connect(mapState, mapDispatch),
	withContent({ content: pageContent }),
	withScrollTop
)(withAccountSelection(CreditImportedPaymentsPage));
